import React, { useEffect, useState, useRef, forwardRef } from 'react';
// @ts-expect-error - ImageCropModal is a JSX file without TypeScript declarations
import ImageCropModal from '../modals/ImageCropModal';
// @ts-expect-error - getCroppedImg is a JS file without TypeScript declarations
import getCroppedImg from '../../utils/cropImage';
import { Button } from '@/components/ui/button';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { toast } from 'sonner';
import { validateImageFile, validateBase64Size, IMAGE_LIMITS } from '@/src/utils/imageValidation';
import {
  chooseOutputFormat,
  hasAlpha,
  resolveUploadedImageUrl,
} from '../../utils/imageEncoding';

interface ImageUploaderProps {
  image?: string | null;
  image_type?: string | null;
  merchantName?: string | null;
  elementID?: string | null;
  desiredHeight: number;
  desiredWidth: number;
  setImage: (image: string | null) => void;
  setResizedImage: (image: string | null) => void;
  aspect?: number; // Optional aspect ratio (default 16/9)
  onDelete?: () => Promise<void>; // Optional callback to delete image from backend
}

const ImageUploader = forwardRef<HTMLDivElement, ImageUploaderProps>((props, ref) => {
  const [imageUrl, setImageUrl] = useState<string | null>(props.image || null);
  const [showCropModal, setShowCropModal] = useState<boolean>(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isImageLoading, setIsImageLoading] = useState<boolean>(true);
  const [naturalAspectRatio, setNaturalAspectRatio] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Sync props.image changes to internal state (e.g. Profile photo loaded async from Clerk)
  useEffect(() => {
    if (props.image !== undefined) {
      setImageUrl(props.image || null);
      if (props.image) {
        setIsImageLoading(true);
      }
    }
  }, [props.image]);

  useEffect(() => {
    if (
      props.image_type &&
      props.merchantName &&
      props.elementID
    ) {
      const legacyImageUrl = `${import.meta.env.VITE_APP_IMAGE_URL}/${props.image_type}/${
        props.merchantName
      }_${props.elementID}.png?timestamp=${Date.now().toString()}`;
      const imageUrl = resolveUploadedImageUrl({
        persistedUrl: props.image,
        legacyUrl: legacyImageUrl,
      });
      setImageUrl(imageUrl);
      setIsImageLoading(Boolean(imageUrl)); // Reset loading state when URL changes
      // For existing images, use the same URL as original since we don't have the actual original
      setOriginalImageUrl(imageUrl);
    }
  }, [props.image, props.image_type, props.merchantName, props.elementID]);

  const processImageFile = async (file: File): Promise<void> => {
    // Validate file using centralized validation
    const validation = validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    // Clean up previous object URLs
    if (originalImageUrl && originalImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(originalImageUrl);
    }

    // Create a temporary URL for the selected image
    const tempUrl = URL.createObjectURL(file);
    setTempImageUrl(tempUrl);
    setOriginalImageUrl(tempUrl); // Store the original image
    setFileName(file.name);
    setShowCropModal(true);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    // Selecting the same file again after canceling the crop must fire change.
    e.target.value = '';
    if (file) await processImageFile(file);
  };

  const handleButtonClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    // Child dragenter/dragleave events bubble too. Only clear the highlight
    // once the file has left the entire uploader, not its text or controls.
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await processImageFile(files[0]);
    }
  };

  const handleCropComplete = async (croppedAreaPixels: any): Promise<void> => {
    try {
      // Validate croppedAreaPixels before processing
      if (!croppedAreaPixels || !croppedAreaPixels.width || !croppedAreaPixels.height) {
        toast.error('Please adjust the crop area and try again');
        return;
      }

      // Crop coordinates are already scaled to natural image size by ImageCropModal
      const finalCropPixels = croppedAreaPixels;

      // Get the cropped image - use PNG for logos to preserve transparency
      const outputFormat = props.image_type === 'logo' ? 'image/png' : 'image/jpeg';
      const croppedImage = await getCroppedImg(
        tempImageUrl,
        finalCropPixels,
        0, // rotation
        { horizontal: false, vertical: false }, // flip
        undefined, // use default config
        outputFormat,
      );

      if (!croppedImage) {
        toast.error('Failed to crop image - please try again');
        return;
      }

      // Validate the cropped image dimensions (skip for logo images which allow any size)
      const img = document.createElement('img');
      img.src = croppedImage;

      await new Promise<void>((resolve, reject) => {
        img.onload = function () {
          // Skip dimension validation for logo images - they can be any size
          if (props.image_type === 'logo') {
            resolve();
            return;
          }
          if (img.height < props.desiredHeight || img.width < props.desiredWidth) {
            toast.error(
              `Image dimensions must be at least ${props.desiredWidth} x ${props.desiredHeight}`,
            );
            reject(new Error('Image dimensions too small'));
          } else {
            resolve();
          }
        };
        img.onerror = () => {
          reject(new Error('Failed to load cropped image'));
        };
      });

      // Convert the cropped image to base64 for setResizedImage (like the older version)
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const imgElement = document.createElement('img');

      await new Promise<void>((resolve, reject) => {
        imgElement.onload = () => {
          try {
            // Check if component is still mounted before setState
            if (!isMountedRef.current) {
              reject(new Error('Component unmounted'));
              return;
            }

            canvas.width = imgElement.width;
            canvas.height = imgElement.height;
            ctx?.drawImage(imgElement, 0, 0);
            const output = chooseOutputFormat({
              imageType: props.image_type,
              hasAlpha: hasAlpha(canvas),
            });
            const base64String = canvas.toDataURL(output.mimeType, output.quality);

            // Validate base64 size before setting
            const sizeValidation = validateBase64Size(base64String);
            if (!sizeValidation.valid) {
              toast.error(sizeValidation.error);
              reject(new Error(sizeValidation.error));
              return;
            }

            // Update the image states only if still mounted
            props.setImage(croppedImage); // Object URL for display
            props.setResizedImage(base64String); // Base64 for backend
            setImageUrl(croppedImage);
            resolve();
          } catch (error) {
            reject(error);
          }
        };

        imgElement.onerror = () => {
          reject(new Error('Failed to load image for base64 conversion'));
        };

        imgElement.src = croppedImage;
      });
    } catch (error) {
      console.error('Error cropping image:', error);

      // Provide specific user feedback based on error type
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      if (errorMessage.includes('unmounted')) {
        toast.error('Operation cancelled - please try again');
      } else if (errorMessage.includes('size')) {
        toast.error('Image is too large - please select a smaller image');
      } else if (errorMessage.includes('load')) {
        toast.error('Failed to load image - please try a different file');
      } else {
        toast.error('Failed to process image - please try again');
      }
    } finally {
      // Clean up temp URL only if it's different from original
      if (tempImageUrl && tempImageUrl !== originalImageUrl) {
        URL.revokeObjectURL(tempImageUrl);
      }
      setTempImageUrl(null);
      setShowCropModal(false);
    }
  };

  const handleRemove = async (): Promise<void> => {
    // Call backend delete if onDelete callback is provided
    if (props.onDelete) {
      try {
        await props.onDelete();
      } catch (error) {
        console.error('Failed to delete image from backend:', error);
        toast.error('Failed to delete image. Please try again.');
        return; // Don't clear local state if backend delete failed
      }
    }

    setImageUrl(null);
    setOriginalImageUrl(null);
    setFileName('');
    setNaturalAspectRatio(null);
    props.setImage(null);
    props.setResizedImage(null);

    // Clean up any object URLs
    if (originalImageUrl && originalImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(originalImageUrl);
    }
  };

  // Calculate aspect ratio safely
  const calculateAspectRatio = (): number => {
    // Use provided aspect ratio if available
    if (props.aspect !== undefined && !isNaN(props.aspect) && props.aspect > 0) {
      return props.aspect;
    }

    // Logo images use 16:9 aspect ratio
    if (props.image_type === 'logo') {
      return 16 / 9;
    }

    const width = props.desiredWidth;
    const height = props.desiredHeight;

    if (width && height && !isNaN(width) && !isNaN(height) && width > 0 && height > 0) {
      return width / height;
    }

    // Default aspect ratio if props are invalid
    return 16 / 9; // Changed to 16:9 as specified
  };

  return (
    <div className="sm:col-span-2" ref={ref}>
      <ImageCropModal
        open={showCropModal}
        setOpen={setShowCropModal}
        image={tempImageUrl}
        onCropComplete={handleCropComplete}
        aspect={calculateAspectRatio()}
        freeAspect={props.image_type === 'logo'}
      />

      <div
        role="group"
        aria-label="Image upload"
        className={`rounded-md border bg-paper-inset transition-colors ${
          isDragging ? 'border-sage ring-1 ring-sage' : 'border-edge'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {imageUrl == null ? (
          // No image state - Upload interface
          <button
            type="button"
            onClick={handleButtonClick}
            className={`flex h-[205px] w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-md bg-paper-inset p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage ${
              isDragging ? 'bg-accent/10' : ''
            }`}
          >
            {/* Image Icon */}
            <span className="relative h-[50px] w-[50px] overflow-hidden">
              <svg width="50" height="50" viewBox="0 0 50 50" fill="none">
                <path
                  d="M4 12C4 9.79086 5.79086 8 8 8H42C44.2091 8 46 9.79086 46 12V38C46 40.2091 44.2091 42 42 42H8C5.79086 42 4 40.2091 4 38V12Z"
                  stroke="#3A3429"
                  strokeWidth="2"
                />
                <circle cx="37" cy="17" r="3" fill="#3A3429" />
                <path
                  d="M4 32L12 24L20 32L32 20L46 34V38C46 40.2091 44.2091 42 42 42H8C5.79086 42 4 40.2091 4 38V32Z"
                  fill="#3A3429"
                />
              </svg>
            </span>

            {/* Text content */}
            <span className="flex flex-col items-start gap-2 self-stretch">
              <span className="self-stretch text-center font-inter text-base font-medium leading-6 text-ink-secondary">
                {isDragging ? 'Drop image here' : 'Drag and drop a file or click to upload'}
              </span>
              <span className="self-stretch text-center font-inter text-sm font-medium leading-5 text-ink-secondary">
                {IMAGE_LIMITS.SUPPORTED_FORMATS.map((format: string) =>
                  format.replace('image/', '').toUpperCase(),
                ).join(', ')}{' '}
                (max {IMAGE_LIMITS.MAX_FILE_SIZE_MB}MB)
              </span>
            </span>

            {/* Upload button */}
            <span
              className="inline-flex items-center justify-center gap-1 rounded-md border border-edge bg-paper-muted px-2 py-1 text-center font-inter text-sm font-medium leading-5 text-ink-muted transition-all duration-200 hover:bg-paper-inset"
            >
              Upload
            </span>
          </button>
        ) : (
          // Image uploaded state - Display image with controls
          <div
            className={`flex flex-col items-center  gap-4 p-2 ${
              props.image_type === 'logo' ? 'xl:flex-row' : 'sm:flex-row'
            }`}
          >
            {/* Image - 2/3 width */}
            <div className={`w-full  ${props.image_type === 'logo' ? 'xl:w-2/3' : 'sm:w-2/3'}`}>
              <div
                className="relative w-full overflow-hidden rounded-md bg-paper-inset"
                style={{
                  aspectRatio: naturalAspectRatio ? naturalAspectRatio : '16 / 9',
                  minHeight: '120px',
                  maxHeight: '300px',
                }}
              >
                {isImageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-paper-inset">
                    <svg
                      className="h-8 w-8 animate-spin text-ink-muted"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  </div>
                )}
                <img
                  src={imageUrl}
                  draggable={false}
                  alt="Uploaded content"
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    if (img.naturalWidth && img.naturalHeight) {
                      setNaturalAspectRatio(img.naturalWidth / img.naturalHeight);
                    }
                    setIsImageLoading(false);
                  }}
                  onError={(e) => {
                    console.error("[ImageUploader] img onError — failed to load:", imageUrl, e);
                    setImageUrl(null);
                    setIsImageLoading(false);
                    setNaturalAspectRatio(null);
                  }}
                  className={`h-full w-full object-contain ${
                    isImageLoading ? 'invisible' : 'visible'
                  }`}
                />

                {/* Remove button (X) */}
                <button
                  type="button"
                  onClick={handleRemove}
                  className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#F5EDEB] text-white shadow-md transition-colors duration-200 hover:bg-red-600"
                  title="Remove image"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Controls - 1/3 width */}
            <div
              className={`flex w-full  flex-col items-center justify-center gap-3  ${
                props.image_type === 'logo' ? 'xl:w-1/3' : 'sm:w-1/3'
              }`}
            >
              {/* Image name */}
              <div>
                <p className="break-all text-sm font-bold text-ink-muted">
                  {fileName || 'uploaded-image.png'}
                </p>
              </div>

              <div className="flex flex-col gap-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleButtonClick}
                  className="h-[28px] gap-x-1 px-3 text-sm"
                >
                  Replace Image
                </Button>

                {/* Edit button */}
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!imageUrl && !originalImageUrl}
                  onClick={() => {
                    // Use original image if available, otherwise use current image
                    const imageToEdit = originalImageUrl || imageUrl;
                    if (imageToEdit) {
                      setTempImageUrl(imageToEdit);
                      setShowCropModal(true);
                    }
                  }}
                  className="h-[28px] gap-x-1 px-3 text-sm"
                >
                  Edit Crop
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleUpload}
        accept={IMAGE_LIMITS.SUPPORTED_FORMATS.join(',')}
      />
    </div>
  );
});

ImageUploader.displayName = 'ImageUploader';

export default ImageUploader;
